import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export interface Certificate {
  id: string;
  user_id: string;
  curriculum_id: string;
  curriculum_title: string;
  user_name: string;
  issued_at: string;
  certificate_number: string;
}

export function useCertificates() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["certificates", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("certificates")
        .select("*")
        .eq("user_id", user.id)
        .order("issued_at", { ascending: false });

      if (error) throw error;
      return data as Certificate[];
    },
    enabled: !!user?.id,
  });
}

export function useCheckAndIssueCertificate() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      curriculumId,
      curriculumTitle,
      userName,
    }: {
      curriculumId: string;
      /** IGNORED — the RPC reads the title from curricula server-side. */
      curriculumTitle?: string;
      /** IGNORED — the RPC reads the name from profiles server-side. */
      userName?: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      // Issue via the SECURITY DEFINER RPC. The completion check, the
      // certificate number and the insert all now happen SERVER-SIDE.
      //
      // Previously this hook checked is_curriculum_complete client-side,
      // generated the number client-side, and INSERTed directly — and the only
      // INSERT policy on certificates was WITH CHECK (user_id = auth.uid()), so
      // any user could POST a certificate for any curriculum with any number.
      // Migration 20260727000011 drops that policy, making this RPC the only path.
      //
      // COUPLED: this change and that migration must deploy together.
      // NOTE: cast until the generated Supabase types include the new RPC.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("issue_certificate", {
        _curriculum_id: curriculumId,
      });

      if (error) {
        // The RPC raises 42501 when the curriculum is not complete. Preserve the
        // hook's existing contract of returning null in that case so callers can
        // distinguish "not eligible" from "something broke".
        const message = String(error.message ?? "");
        if (message.includes("not complete")) return null;
        throw error;
      }

      // The RPC is idempotent: if a certificate already exists it returns that
      // row rather than erroring, so an already-issued certificate now comes
      // back here instead of surfacing as null.
      return (data as Certificate) ?? null;
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ["certificates"] });
        queryClient.invalidateQueries({ queryKey: ["curriculum-enrollments"] });
      }
    },
  });
}

/**
 * Authoritative curriculum-completion check.
 *
 * The Get Certificate button used to be gated on a page-level
 * completedCourses/totalCourses calculation while the mutation gated on the
 * is_curriculum_complete RPC. Those two can disagree — notably
 * get_course_progress returns 0 (never 100) for a course with zero modules — so
 * the button could be enabled while the mutation silently returned null.
 * Drive the UI from this hook so both use the same source of truth.
 */
export function useIsCurriculumComplete(curriculumId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["is-curriculum-complete", curriculumId, user?.id],
    enabled: !!curriculumId && !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("is_curriculum_complete", {
        _user_id: user!.id,
        _curriculum_id: curriculumId!,
      });
      if (error) throw error;
      return data === true;
    },
  });
}
